import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaTrash, FaBookOpen } from 'react-icons/fa';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import { BankTransaction } from '../../../models/BankTransaction';
import { BankAccount } from '../../../models/BankAccount';
import { Ledger } from '../../../models/Ledger';
import { FiscalYear } from '../../../models/FiscalYear';
import { ReferenceNumberConfigRestDataProvider } from '../../../data/ReferenceNumberConfigRestDataProvider';
import { BankTransactionRestDataProvider, BookTransactionLine } from '../../../data/BankTransactionRestDataProvider';
import SelectInput from '../../widgets/SelectInput';

interface DistributionRow {
  id: number;
  ledgerId: string;
  description: string;
  amount: string; // kept as string while editing
}

interface Props {
  transaction: BankTransaction;
  bankAccounts: BankAccount[];
  ledgers: Ledger[];
  fiscalYears: FiscalYear[];
  onClose: () => void;
  onBooked: (tx: BankTransaction, referenceNumber: string) => void;
}

let rowCounter = 1;

const BankTransactionBookingModal: React.FC<Props> = ({
  transaction,
  bankAccounts,
  ledgers,
  fiscalYears,
  onClose,
  onBooked,
}) => {
  const { t } = useTranslation(['common', 'screens']);

  const refProvider = useMemo(() => new ReferenceNumberConfigRestDataProvider(), []);
  const txProvider  = useMemo(() => new BankTransactionRestDataProvider(), []);

  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [refLoading, setRefLoading]           = useState(true);

  // Derive the bank account and its ledger code for the auto bank entry.
  const bankAccount = useMemo(
    () => bankAccounts.find(a => a.id === transaction.bankAccountId),
    [bankAccounts, transaction.bankAccountId],
  );
  const bankLedger = useMemo(
    () => ledgers.find(l => l.code === bankAccount?.ledgerCode),
    [ledgers, bankAccount],
  );

  // Auto-select fiscal year matching the transaction value date.
  const autoFiscalYear = useMemo(() => {
    if (!fiscalYears.length) return null;
    const txDate = new Date(transaction.valueDate);
    return (
      fiscalYears.find(
        fy => txDate >= new Date(fy.start) && txDate <= new Date(fy.end),
      ) ?? fiscalYears[0]
    );
  }, [fiscalYears, transaction.valueDate]);

  const [fiscalYearId, setFiscalYearId] = useState<string>('');

  useEffect(() => {
    if (autoFiscalYear) setFiscalYearId(autoFiscalYear.id);
  }, [autoFiscalYear]);

  // Default description derived from the transaction — used to pre-fill new rows.
  const defaultDescription = transaction.narrativeRemittanceInfo ?? transaction.customerReference ?? '';

  // Distribution rows — start with one pre-filled row.
  const [rows, setRows] = useState<DistributionRow[]>(() => [
    { id: rowCounter++, ledgerId: '', description: defaultDescription, amount: '' },
  ]);

  const [saving, setSaving]             = useState(false);
  const [confirmOpen, setConfirmOpen]   = useState(false);

  // Peek the BANK reference number once on mount.
  useEffect(() => {
    refProvider
      .peek('BANK')
      .then(r => setReferenceNumber(r.formattedNumber))
      .catch(() => setReferenceNumber('—'))
      .finally(() => setRefLoading(false));
  }, [refProvider]);

  // ── Row helpers ────────────────────────────────────────────────────────────

  const addRow = () =>
    setRows(r => [...r, { id: rowCounter++, ledgerId: '', description: defaultDescription, amount: '' }]);

  const removeRow = (id: number) =>
    setRows(r => r.filter(row => row.id !== id));

  const updateRow = (id: number, field: keyof Omit<DistributionRow, 'id'>, value: string) =>
    setRows(r => r.map(row => (row.id === id ? { ...row, [field]: value } : row)));

  // ── Totals ─────────────────────────────────────────────────────────────────

  const rowTotal = useMemo(
    () => rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0),
    [rows],
  );
  const remaining   = transaction.amount - rowTotal;
  const isBalanced  = Math.abs(remaining) < 0.005;
  const canBook     =
    isBalanced &&
    !!fiscalYearId &&
    rows.every(r => r.ledgerId && parseFloat(r.amount) > 0);

  const isCredit = transaction.creditDebitIndicator === 'C' || transaction.creditDebitIndicator === 'RD';

  // ── Book flow ──────────────────────────────────────────────────────────────

  const handleConfirmBook = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const lines: BookTransactionLine[] = rows.map(r => ({
        ledgerId:    r.ledgerId,
        description: r.description,
        amount:      parseFloat(r.amount),
      }));
      const result = await txProvider.book(transaction.id, { fiscalYearId, lines });
      onBooked({ ...transaction, ledgerReferenceCode: result.referenceNumber }, result.referenceNumber);
    } catch {
      // toast is fired by BaseRestDataProvider on error
    } finally {
      setSaving(false);
    }
  };

  const signedAmount = isCredit ? transaction.amount : -transaction.amount;

  return (
    <>
      <PopupDialog
        open
        title={t('screens:bankTransactions.booking.title')}
        onClose={saving ? () => {} : onClose}
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canBook || saving || refLoading}
              onClick={() => setConfirmOpen(true)}
            >
              <FaBookOpen style={{ marginRight: 6 }} />
              {saving
                ? t('screens:bankTransactions.booking.booking')
                : t('screens:bankTransactions.booking.book')}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={saving}
              onClick={onClose}
            >
              {t('common:cancel')}
            </button>
          </>
        }
      >
        {/* ── Transaction summary ── */}
        <div className="bank-booking-summary">
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.referenceNumber')}</span>
            <span className="bank-booking-summary-value bank-booking-ref">
              {refLoading ? '…' : referenceNumber}
            </span>
          </div>
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.transactionDate')}</span>
            <span className="bank-booking-summary-value">{transaction.valueDate}</span>
          </div>
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.counterparty')}</span>
            <span className="bank-booking-summary-value">
              {transaction.narrativeCounterpartyName || transaction.customerReference || '—'}
            </span>
          </div>
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.amount')}</span>
            <span className={`bank-booking-summary-value ${signedAmount >= 0 ? 'bank-transactions-amount--positive' : 'bank-transactions-amount--negative'}`}>
              {signedAmount >= 0 ? '+' : ''}
              {signedAmount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {' '}{transaction.currency}
            </span>
          </div>
        </div>

        {/* ── Fiscal year selector ── */}
        <div className="mb-3 mt-3">
          <label className="form-label">{t('screens:bankTransactions.booking.fiscalYear')}</label>
          <select
            className="form-select form-select-sm"
            value={fiscalYearId}
            onChange={e => setFiscalYearId(e.target.value)}
            disabled={saving}
          >
            <option value="">{t('screens:bankTransactions.booking.selectFiscalYear')}</option>
            {fiscalYears.map(fy => (
              <option key={fy.id} value={fy.id}>{fy.name}</option>
            ))}
          </select>
        </div>

        {/* ── Bank-side entry (read-only) ── */}
        <div className="bank-booking-bank-entry mb-2">
          <strong>{t('screens:bankTransactions.booking.bankEntry')}</strong>
          <div className="bank-booking-bank-entry-detail">
            <span>{bankLedger ? `${bankLedger.code} — ${bankLedger.name}` : (bankAccount?.ledgerCode ?? '—')}</span>
            <span className="bank-booking-direction">
              {isCredit
                ? t('screens:bankTransactions.booking.debet')
                : t('screens:bankTransactions.booking.credit')}
            </span>
            <span className="bank-booking-amount">
              {transaction.amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* ── Distribution rows ── */}
        <div className="bank-booking-lines-header">
          <strong>{t('screens:bankTransactions.booking.distributionLines')}</strong>
        </div>
        <table className="table table-sm bank-booking-lines-table">
          <thead>
            <tr>
              <th>{t('screens:bankTransactions.booking.ledger')}</th>
              <th>{t('screens:bankTransactions.booking.description')}</th>
              <th className="text-end">{t('screens:bankTransactions.booking.amount')}</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id}>
                <td>
                  <SelectInput
                    label=""
                    small
                    value={row.ledgerId}
                    options={ledgers.map(l => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
                    onChange={(val: string) => updateRow(row.id, 'ledgerId', val)}
                    disabled={saving}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={row.description}
                    onChange={e => updateRow(row.id, 'description', e.target.value)}
                    disabled={saving}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control form-control-sm text-end"
                    value={row.amount}
                    min={0}
                    step={0.01}
                    onChange={e => updateRow(row.id, 'amount', e.target.value)}
                    disabled={saving}
                  />
                </td>
                <td>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => removeRow(row.id)}
                      disabled={saving}
                    >
                      <FaTrash />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} className="text-end">
                <strong>{t('screens:bankTransactions.booking.total')}</strong>
              </td>
              <td className={`text-end fw-bold ${!isBalanced && rowTotal > 0 ? 'text-danger' : isBalanced ? 'text-success' : ''}`}>
                {rowTotal.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td />
            </tr>
            {!isBalanced && rowTotal > 0 && (
              <tr>
                <td colSpan={2} className="text-end text-danger small">
                  {t('screens:bankTransactions.booking.remaining')}
                </td>
                <td className="text-end text-danger small">
                  {remaining.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td />
              </tr>
            )}
          </tfoot>
        </table>

        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={addRow}
          disabled={saving}
        >
          <FaPlus style={{ marginRight: 4 }} />
          {t('screens:bankTransactions.booking.addLine')}
        </button>
      </PopupDialog>

      {/* ── Confirmation dialog ── */}
      {confirmOpen && (
        <PopupDialog
          open
          title={t('screens:bankTransactions.booking.confirmTitle')}
          onClose={() => setConfirmOpen(false)}
          actions={
            <>
              <button
                type="button"
                className="btn btn-primary me-2"
                onClick={handleConfirmBook}
              >
                <FaBookOpen style={{ marginRight: 6 }} />
                {t('screens:bankTransactions.booking.confirmBook')}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setConfirmOpen(false)}
              >
                {t('common:cancel')}
              </button>
            </>
          }
        >
          <p>
            {t('screens:bankTransactions.booking.confirmMessage', {
              reference: referenceNumber,
              amount: transaction.amount.toLocaleString('nl-NL', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            })}
          </p>
        </PopupDialog>
      )}
    </>
  );
};

export default BankTransactionBookingModal;








